/**
 * Transaction Column Definitions
 *
 * Column definitions for the HistoricalTransaction DataTable.
 */

import { type ColumnDef } from "@tanstack/react-table";
import type { HistoricalTransaction } from "@/services/paymentService";
import { StatusBadge } from "../components/StatusBadge";
import { PaymentTypeIcon } from "../components/PaymentTypeIcon";
import {
  formatDate,
  getCallsign,
  getName,
  formatNominal,
  getPaymentType,
  formatPaymentType,
  getSettlementTime,
} from "../utils/transactionUtils";

export const transactionColumns: ColumnDef<HistoricalTransaction>[] = [
  {
    id: "callsign",
    accessorFn: (row) => getCallsign(row),
    header: "Callsign",
    cell: ({ row }) => (
      <span className="font-medium">{getCallsign(row.original)}</span>
    ),
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = getCallsign(rowA.original);
      const b = getCallsign(rowB.original);
      return a.localeCompare(b);
    },
  },
  {
    id: "nama",
    accessorFn: (row) => getName(row),
    header: "Nama",
    cell: ({ row }) => getName(row.original),
    enableSorting: false,
  },
  {
    accessorKey: "province",
    header: "ORDA",
    cell: ({ row }) => row.original.province || "-",
    enableSorting: true,
  },
  {
    accessorKey: "kabkot",
    header: "ORLOK",
    cell: ({ row }) => row.original.kabkot || "-",
    enableSorting: true,
  },
  {
    id: "createdAt",
    accessorFn: (row) => row.createdAt,
    header: "Tanggal Transaksi",
    cell: ({ row }) => formatDate(row.original.createdAt),
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const a = new Date(rowA.original.createdAt).getTime();
      const b = new Date(rowB.original.createdAt).getTime();
      return a - b;
    },
  },
  {
    id: "settlementTime",
    accessorFn: (row) => getSettlementTime(row) ?? "",
    header: "Waktu Settlement",
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {getSettlementTime(row.original) || "-"}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
    enableSorting: true,
  },
  {
    id: "paymentType",
    accessorFn: (row) => getPaymentType(row),
    header: "Jenis Pembayaran",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <PaymentTypeIcon transaction={row.original} />
        <span className="text-xs">
          {formatPaymentType(getPaymentType(row.original))}
        </span>
      </div>
    ),
    enableSorting: false,
  },
  {
    id: "nominal",
    accessorFn: (row) => {
      const amount =
        row.payementData?.transaction_details?.gross_amount ??
        row.payementData?.amount ??
        row.paymentGatewayData?.gross_amount ??
        row.paymentGatewayData?.amount ??
        0;
      return typeof amount === "string" ? parseInt(amount, 10) : amount;
    },
    header: "Nominal",
    cell: ({ row }) => formatNominal(row.original),
    enableSorting: false,
  },
];
